<template>
  <VTextField
    v-model="displayValue"
    dense
    outlined
    hide-details="auto"
    placeholder="Email, phone, order #, or proof ID"
    class="caption mx-1 mt-1 mb-1 search-box"
    style="font-size: 12px"
    :type="inputType"
    :loading="isLoading"
    :readonly="readonly"
    :clearable="!readonly"
    :error-messages="errorMsg ? [errorMsg] : []"
    @focus="onFocus"
    @blur="onBlur"
    @keydown.enter="onEnter"
    @click:clear="clearInput"
  >
    <template #prepend-inner>
      <VTooltip v-if="formatLabel" bottom open-delay="300">
        <template #activator="{ on, attrs }">
          <VIcon
            :color="formatColor"
            class="mr-1 search-box-icon-left"
            v-bind="attrs"
            v-on="on"
          >
            {{ inputIcon }}
          </VIcon>
        </template>
        <span>{{ formatLabel }}</span>
      </VTooltip>
      <VIcon v-else color="grey" class="mr-1 search-box-icon-left">
        $search
      </VIcon>
    </template>
  </VTextField>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'

import { normalizeUSPhone } from '@/store/modules/salesforce'
import {
  detectFormat,
  formatOrder,
  formatPhone,
  normalizeOrderNumber
} from '@/utils/searchDetection'

export default defineComponent({
  props: {
    initialEmail: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['live-filter', 'search'],
  setup(props, { emit }) {
    const displayValue = ref(props.initialEmail)
    const rawValue = ref(props.initialEmail)
    const inputType = ref(props.initialEmail.includes('@') ? 'email' : 'text')
    const isLoading = ref(false)
    const errorMsg = ref<string | null>(null)
    const isFocused = ref(false)
    let lastFetched = props.initialEmail

    const format = computed(() => detectFormat(displayValue.value))

    const inputIcon = computed(() => {
      switch (format.value) {
        case 'email':
          return '$envelope'
        case 'phone':
          return '$phone'
        case 'order':
          return '$shopping-cart'
        case 'proofId':
          return '$pen-square'
        default:
          return '$search'
      }
    })

    const formatLabel = computed(() => {
      switch (format.value) {
        case 'email':
          return 'Email'
        case 'phone':
          return 'Phone'
        case 'order':
          return 'Order #'
        case 'proofId':
          return 'Proof ID'
        default:
          return null
      }
    })

    const formatColor = computed(() => {
      switch (format.value) {
        case 'email':
          return 'blue-grey'
        case 'phone':
          return 'teal'
        case 'order':
          return 'indigo'
        case 'proofId':
          return 'deep-purple'
        default:
          return 'grey'
      }
    })

    // No hint needed — the formatted display is sufficient

    function onFocus() {
      isFocused.value = true
      // Switch to text input while editing so non-email chars (_, -, etc.) work
      inputType.value = 'text'
      // Show raw value while editing
      displayValue.value = rawValue.value
    }

    function applyFormatting() {
      const val = displayValue.value?.trim() || ''
      // Use detectFormat first to respect priority (order before phone)
      const fmt = detectFormat(val)
      if (fmt === 'phone') {
        const normalized = normalizeUSPhone(val)
        if (normalized) {
          rawValue.value = normalized
          displayValue.value = formatPhone(normalized)
          return
        }
      }
      if (fmt === 'order') {
        const normalized = normalizeOrderNumber(val)
        if (normalized) {
          rawValue.value = normalized.value
          displayValue.value = formatOrder(normalized.value)
          return
        }
      }
      // No formatting for email/proof — raw = display
      rawValue.value = val
    }

    let forceSearch = false

    function onEnter(e: KeyboardEvent) {
      forceSearch = e.shiftKey
      // Blur the input to trigger onBlur (same as clicking away)
      ;(e.target as HTMLInputElement)?.blur()
    }

    async function onBlur() {
      const force = forceSearch
      forceSearch = false
      isFocused.value = false
      // Don't clear live filter here — let App.vue's onSearch decide
      // whether to keep it (local match) or clear it (API search)
      // Clean up pasted input: trim whitespace and normalize
      displayValue.value = displayValue.value?.trim() || ''
      inputType.value = displayValue.value.includes('@') ? 'email' : 'text'
      applyFormatting()

      if (props.readonly) return
      const value = rawValue.value
      if (!value || value === lastFetched) return

      errorMsg.value = null

      // Emit search event — App.vue decides whether to do API search
      // or rely on local filter results. Validation only happens for
      // API searches; local filter matches skip it.
      const fmt = detectFormat(value)
      emit('search', { value, format: fmt, force })
    }

    /**
     * Called by App.vue after a search completes (API or local).
     * Updates the display value and tracks what was last fetched.
     */
    function onSearchComplete(resolvedEmail: string, displayAs?: string) {
      displayValue.value = displayAs || resolvedEmail
      rawValue.value = displayAs || resolvedEmail
      lastFetched = rawValue.value
      inputType.value = resolvedEmail.includes('@') ? 'email' : 'text'
    }

    function setLoading(loading: boolean) {
      isLoading.value = loading
    }

    function setError(msg: string | null) {
      errorMsg.value = msg
    }

    function clearInput() {
      displayValue.value = ''
      rawValue.value = ''
      errorMsg.value = null
      emit('live-filter', '')
    }

    /**
     * Update the display value without triggering a lookup.
     * Used when selecting a match from the disambiguation dropdown.
     */
    function setEmail(newEmail: string) {
      displayValue.value = newEmail
      rawValue.value = newEmail
      lastFetched = newEmail
      inputType.value = newEmail.includes('@') ? 'email' : 'text'
    }

    // Emit live-filter events as the user types (while focused)
    watch(displayValue, (val) => {
      if (isFocused.value) {
        emit('live-filter', val?.trim() || '')
      }
    })

    // Apply initial formatting if value is a phone/order
    applyFormatting()

    return {
      displayValue,
      setEmail,
      onSearchComplete,
      setLoading,
      setError,
      inputType,
      inputIcon,
      formatLabel,
      formatColor,
      isLoading,
      errorMsg,
      onFocus,
      onBlur,
      onEnter,
      clearInput
    }
  }
})
</script>

<style scoped>
/* Vertically center everything inside the text field on one midline */
.search-box::v-deep .v-input__prepend-inner,
.search-box::v-deep .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}

/* Left icon: slightly larger than x-small default for better presence */
.search-box-icon-left {
  font-size: 13px !important;
  width: 16px;
}

/* Clear × icon: match the left icon's visual weight */
.search-box::v-deep .v-input__icon--clear .v-icon {
  font-size: 16px;
  opacity: 0.5;
}
.search-box::v-deep .v-input__icon--clear .v-icon:hover {
  opacity: 0.85;
}
</style>
